// Snooze 控制元件（APP#19）— per-device 一鍵暫停告警入口（裝置卡 / 事件頁共用）。
//
// 兩態：
//   - 未 snooze → 「暫停告警」按鈕，點開快選選單（30 分/1 時/2 時/4 時/自訂分鐘）。
//   - snooze 中 → SnoozeBadge 顯示剩餘倒數（aria-live）+「提前恢復」一鍵清除。
//
// 抑制只在本機（門鈴卡/紅點）；事件仍正常入軸。後端推播閘是 Scott follow-up。

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SNOOZE_MAX_MINUTES,
  SNOOZE_MIN_MINUTES,
  SNOOZE_PRESETS_MINUTES,
  clearSnooze,
  getSnoozeUntil,
  getSnoozeVersion,
  setSnooze,
  snoozeRemaining,
  subscribeSnooze,
} from './snooze';
import './devices.css';

/** 剩餘倒數的 i18n 文案（snoozeRemaining 數值 → t() 語意字串）。 */
function useRemainingLabel(until: number): string {
  const { t } = useTranslation();
  // 每 30s tick 一次讓倒數隨時間遞減（store 變更已另由 useSyncExternalStore 觸發）。
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  const r = snoozeRemaining(until);
  if (r.unit === 'expired') return t('snooze.remainingExpired');
  if (r.unit === 'soon') return t('snooze.remainingSoon');
  if (r.unit === 'hours') return t('snooze.remainingHours', { count: r.value });
  return t('snooze.remainingMinutes', { count: r.value });
}

export function SnoozeBadge({ deviceId, until }: { deviceId: string; until: number }) {
  const { t } = useTranslation();
  const label = useRemainingLabel(until);
  return (
    <span className="sn-badge" data-testid={`snooze-badge-${deviceId}`}>
      <span className="sn-badge-text" role="status" aria-live="polite" aria-label={t('snooze.activeAria', { time: label })}>
        {t('snooze.activeShort', { time: label })}
      </span>
      <button
        type="button"
        className="sn-resume"
        aria-label={t('snooze.resumeAria')}
        onClick={(e) => {
          e.stopPropagation();
          clearSnooze(deviceId);
        }}
      >
        {t('snooze.resume')}
      </button>
    </span>
  );
}

export function SnoozeControl({ deviceId, compact = false }: { deviceId: string; compact?: boolean }) {
  const { t } = useTranslation();
  useSyncExternalStore(subscribeSnooze, getSnoozeVersion);
  const until = getSnoozeUntil(deviceId);
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  // 點選單外 → 收起。
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (until != null) {
    return <SnoozeBadge deviceId={deviceId} until={until} />;
  }

  const apply = (minutes: number) => {
    setSnooze(deviceId, minutes);
    setOpen(false);
    setCustom('');
  };

  const applyCustom = () => {
    const n = Number(custom);
    if (!Number.isFinite(n) || n <= 0) return;
    apply(n);
  };

  return (
    <div className={`sn-control${compact ? ' sn-compact' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="btn-ghost sn-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('snooze.triggerAria')}
        data-testid={`snooze-trigger-${deviceId}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {t('snooze.trigger')}
      </button>
      {open && (
        <div className="sn-menu" role="menu" aria-label={t('snooze.menuAria')} onClick={(e) => e.stopPropagation()}>
          {SNOOZE_PRESETS_MINUTES.map((m) => (
            <button key={m} type="button" role="menuitem" className="sn-menu-item" onClick={() => apply(m)}>
              {m < 60 ? t('snooze.presetMinutes', { count: m }) : t('snooze.presetHours', { count: m / 60 })}
            </button>
          ))}
          <div className="sn-custom">
            <label className="sn-custom-label" htmlFor={`sn-custom-${deviceId}`}>
              {t('snooze.customLabel')}
            </label>
            <input
              id={`sn-custom-${deviceId}`}
              type="number"
              min={SNOOZE_MIN_MINUTES}
              max={SNOOZE_MAX_MINUTES}
              inputMode="numeric"
              className="sn-custom-input"
              value={custom}
              placeholder={t('snooze.customPlaceholder')}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyCustom();
              }}
            />
            <button type="button" className="btn-ghost sn-custom-apply" onClick={applyCustom} disabled={!custom}>
              {t('snooze.customApply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
